import { useState, type CSSProperties } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  DEFAULT_ROBOT_RETURN_LOCATION,
  ROBOT_RETURN_LOCATION_CHOICES,
  ROBOT_RETURN_LOCATION_LABELS,
  type CalendarRenewalDto,
  type RenewalRobotAction,
  type RobotReturnLocation,
} from '@bhops/shared';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { TimeSelect } from '@/components/TimeSelect';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useOptionalToast } from '@/components/Toast';
import { scheduleDatesApi } from '@/api/scheduleDates';
import { extractApiError } from '@/api/client';
import { RenewalPlanList } from './RenewalPlanList';

// 통합 캘린더 재계약 일정 → [재계약 완료](2026-10-22 오너 — "사용자가 직접 완료 처리를 하기 전까지는 무조건
// 미완료 상태로 둬야해. 기간이 지나더라도", "재계약 완료 여부는 서명을 받았을 때 완료 처리로 하거나,
// 콘솔페이지에서 직접 완료 처리를 하는 경우만"). 서버가 한 거래로:
//   · 옛 고객사 로봇 — 회수(정비 입고) 또는 이어서 사용(새 고객사로 넘긴다)
//   · 옛 계약은 '재계약 대체'로 해지, 옛 고객사는 나가 있는 로봇이 없으면 '종료'
//   · 새 계약에 배정된 로봇은 설치 완료(새 고객사 매장에 배치)
// 마친 재계약은 우클릭 [되돌리기]로 없던 일로 돌린다(로봇이 원래 계약으로).

export async function refreshAfterRenewal(qc: QueryClient) {
  await Promise.all([
    qc.invalidateQueries({ queryKey: ['dashboard'] }),
    qc.invalidateQueries({ queryKey: ['infrastructure-orders'] }),
    qc.invalidateQueries({ queryKey: ['service-orders'] }),
    qc.invalidateQueries({ queryKey: ['deployments'] }),
    qc.invalidateQueries({ queryKey: ['robots'] }),
    qc.invalidateQueries({ queryKey: ['customers'] }),
    qc.invalidateQueries({ queryKey: ['contracts'] }),
    qc.invalidateQueries({ queryKey: ['new-schedule'] }),
  ]);
}

export function CompleteRenewalModal({
  renewal: r,
  date: initialDate,
  onClose,
}: {
  renewal: CalendarRenewalDto;
  /** 캘린더 칸의 날짜(YYYY-MM-DD) — 재계약한 날 기본값. */
  date: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useOptionalToast();
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(r.scheduledTime ?? '');
  const [plan, setPlan] = useState<Record<string, RenewalRobotAction>>(() =>
    Object.fromEntries(r.robots.map((x) => [x.robotId, x.action])),
  );
  const [returnLocation, setReturnLocation] = useState<RobotReturnLocation>(
    DEFAULT_ROBOT_RETURN_LOCATION,
  );
  const [error, setError] = useState<string | null>(null);
  const returning = r.robots.filter((x) => (plan[x.robotId] ?? 'RETURN') === 'RETURN');
  const carrying = r.robots.filter((x) => plan[x.robotId] === 'CARRY');

  const save = useMutation({
    mutationFn: () =>
      scheduleDatesApi.completeRenewal(r.id, {
        date,
        time: time || null,
        plan: r.robots.map((x) => ({ robotId: x.robotId, action: plan[x.robotId] ?? 'RETURN' })),
        ...(returning.length ? { returnLocation } : {}),
      }),
    onSuccess: async (res) => {
      await refreshAfterRenewal(qc);
      const parts = [
        res.returned.length ? `회수 ${res.returned.length}대` : null,
        res.carried.length ? `이어서 사용 ${res.carried.length}대` : null,
        res.installed.length ? `설치 ${res.installed.length}대` : null,
      ].filter(Boolean);
      toast?.success(
        `${r.fromCustomerName}의 재계약을 마쳤습니다. 새 고객사는 ${r.toCustomerName}입니다${
          parts.length ? `(${parts.join(' · ')})` : ''
        }. 1분 안에 구글 캘린더에도 반영됩니다.`,
        '재계약 완료',
      );
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="재계약 완료"
      width={540}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            취소
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!date || save.isPending}
            data-testid="renewal-complete-save"
          >
            {save.isPending ? '처리 중…' : '재계약 완료'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={summary} data-testid="renewal-complete-summary">
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <b>{r.fromCustomerName}</b>
            <Icon name="arrow-right" size={13} aria-hidden />
            <b>{r.toCustomerName}</b>
          </span>
          {r.memo && <span style={{ color: 'var(--fg-muted)' }}>{r.memo}</span>}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ ...field, flex: '1 1 150px' }}>
            <span style={labelSt}>재계약한 날</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={input}
              data-testid="renewal-complete-date"
            />
          </label>
          <label style={{ ...field, flex: '2 1 260px' }}>
            <span style={labelSt}>
              시간 <span style={{ fontWeight: 400, color: 'var(--fg-muted)' }}>(선택)</span>
            </span>
            <TimeSelect
              value={time}
              onChange={setTime}
              style={input}
              ariaLabel="재계약 시간 (선택)"
              testId="renewal-complete-time"
            />
          </label>
        </div>

        <div style={field}>
          <span style={labelSt}>{r.fromCustomerName} 로봇</span>
          <RenewalPlanList
            robots={r.robots}
            plan={plan}
            onChange={(id, a) => setPlan((p) => ({ ...p, [id]: a }))}
            testId="renewal-complete-plan"
          />
        </div>

        <div style={field}>
          <span style={labelSt}>{r.toCustomerName}에 설치할 로봇</span>
          {r.incoming.length > 0 ? (
            <div style={incomingList} data-testid="renewal-complete-incoming">
              {r.incoming.map((x) => (
                <span key={x.id} style={incomingChip}>
                  {x.label}
                  {x.modelName ? ` · ${x.modelName}` : ''}
                </span>
              ))}
            </div>
          ) : (
            <span style={hint} data-testid="renewal-complete-no-incoming">
              새 계약에 배정된 로봇이 없습니다
              {carrying.length ? '. 이어서 쓰는 로봇만 넘어갑니다.' : '.'} 새 로봇을 넣으려면 배정
              인박스에서 먼저 배정해 주세요.
            </span>
          )}
        </div>

        {returning.length > 0 && (
          <label style={field}>
            <span style={labelSt}>회수한 로봇을 어디로</span>
            <select
              value={returnLocation}
              onChange={(e) => setReturnLocation(e.target.value as RobotReturnLocation)}
              style={input}
              data-testid="renewal-complete-location"
            >
              {ROBOT_RETURN_LOCATION_CHOICES.map((v) => (
                <option key={v} value={v}>
                  {ROBOT_RETURN_LOCATION_LABELS[v]}
                </option>
              ))}
            </select>
          </label>
        )}

        <span style={hint}>
          {r.fromCustomerName}의 계약은 &lsquo;재계약 대체&rsquo;로 해지되고, 나가 있는 로봇이
          없으면 고객사가 &lsquo;종료&rsquo;로 바뀝니다. 잘못했으면 일정을 우클릭해 [되돌리기]하면
          로봇이 원래 계약으로 돌아갑니다.
        </span>

        {error && (
          <div role="alert" style={errorSt} data-testid="renewal-complete-error">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

/** 마친 재계약 되돌리기 — 확인창(2026-10-22 오너 — "로봇이 원래 계약으로 돌아가야해"). */
export function UndoRenewalDialog({
  renewal: r,
  onClose,
}: {
  renewal: CalendarRenewalDto;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useOptionalToast();
  const [busy, setBusy] = useState(false);
  const viaSignature = r.completedVia === 'SIGNATURE';

  async function confirm() {
    setBusy(true);
    try {
      const res = await scheduleDatesApi.undoRenewal(r.id);
      await refreshAfterRenewal(qc);
      toast?.success(
        `${r.fromCustomerName}의 재계약을 되돌렸습니다.${
          res.keptMaintenanceOrders.length
            ? ` 진행 중인 회수 정비 작업(${res.keptMaintenanceOrders.join(', ')})은 남겨 두었습니다.`
            : ''
        }`,
      );
      onClose();
    } catch (err) {
      setBusy(false);
      throw new Error(extractApiError(err));
    }
  }

  return (
    <ConfirmDialog
      open
      title="재계약 되돌리기"
      message={[
        `${r.fromCustomerName}의 재계약을 마치기 전으로 되돌립니다. 새 고객사는 ${r.toCustomerName}입니다.`,
        '· 회수한 로봇은 다시 옛 매장에 운영 중으로, 이어서 쓴 로봇은 옛 계약으로 돌아갑니다.',
        viaSignature
          ? '· 설치 서명으로 설치된 새 로봇은 서명이 한 일이라 그대로 둡니다.'
          : '· 재계약 때 설치한 새 로봇은 배정만 된 설치 전 상태로 돌아갑니다.',
        "· '재계약 대체'로 해지한 옛 계약은 다시 살아나고, 일정은 미완료로 돌아갑니다.",
      ].join('\n\n')}
      confirmLabel="되돌리기"
      danger
      busy={busy}
      onConfirm={confirm}
      onCancel={onClose}
    />
  );
}

const summary: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-sunken)',
  fontSize: 13,
};
const incomingList: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6 };
const incomingChip: CSSProperties = {
  padding: '3px 10px',
  borderRadius: 999,
  background: 'var(--brand-subtle)',
  color: 'var(--fg-brand)',
  fontSize: 12,
  fontFamily: 'var(--font-mono)',
};
const field: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelSt: CSSProperties = { fontSize: 13, fontWeight: 600 };
const input: CSSProperties = {
  width: '100%',
  height: 36,
  padding: '6px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  boxSizing: 'border-box',
};
const hint: CSSProperties = { fontSize: 12, color: 'var(--fg-muted)' };
const errorSt: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  fontSize: 13,
};
